import FacebookIcon from "@material-ui/icons/Facebook";
import InstagramIcon from "@material-ui/icons/Instagram";
// TwitterIcon's import is removed along with the X link below: an unused import
// is a no-unused-vars warning, and this project builds on Netlify with CI=true,
// which turns every warning into a failed build.
import LinkedInIcon from "@material-ui/icons/LinkedIn";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import { Link } from "react-router-dom";
import styled from "styled-components";

import { mobile } from "../responsive";
import { CUSTOMER_CARE_NUMBER, CUSTOMER_CARE_TEL } from "../constants";
import { FACEBOOK_URL, INSTAGRAM_URL, LINKEDIN_URL, TIKTOK_URL, SUPPORT_PHONE } from "../config/market";
import TikTokIcon from "./icons/TikTokIcon";

// Bare E.164 digits (no "+"/spaces) for wa.me links, derived from the market support phone.
const SUPPORT_WA_NUMBER = String(SUPPORT_PHONE || "").replace(/\D/g, "");

const FooterContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  background-color: #000000;
  width: 100%;

  ${mobile({ padding: "20px 10px", width: "100%" })}
`;

const FooterContent = styled.div`
  text-align: center;
  width: 100%;
  padding-top: 20px;
`;

const Logo = styled.h4`
  color: white;
  margin-bottom: 24px;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 0.5px;
  
  ${mobile({
    fontSize: "17px",
    marginBottom: "20px",
    fontWeight: "700",
    letterSpacing: "1px",
  })}
`;

const Links = styled.div`
  margin-bottom: 20px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 4px;
  padding: 0 40px;
  
  a {
    color: white;
    font-size: 13px;
    font-weight: 500;
    padding: 10px 20px;
    text-decoration: none;
    transition: all 0.3s ease;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 8px;
    border: 1px solid rgba(78, 200, 237, 0.2);

    &:hover {
      color: #4EC8ED;
      text-decoration: none;
      background: rgba(78, 200, 237, 0.15);
      border-color: rgba(78, 200, 237, 0.4);
      transform: translateY(-2px);
    }
  }
  
  .separator {
    display: inline-block; 
    color: #4EC8ED;
    margin: 0 2px; 
    border-left: 5px solid 
    height: 1.5em;
    position: relative;
    top: -0.2em; 
  }
  
  ${mobile({
    flexDirection: "row",
    flexWrap: "wrap",
    gap: "12px",
    margin: "24px 0",
    padding: "0 16px",
    
    "a": {
      width: "calc(50% - 6px)",
      margin: "0",
      fontSize: "13px",
      fontWeight: "500",
      padding: "12px 16px",
      textAlign: "center",
      background: "rgba(255, 255, 255, 0.05)",
      borderRadius: "8px",
      border: "1px solid rgba(78, 200, 237, 0.2)",
      transition: "all 0.3s ease",
      
      "&:hover": {
        background: "rgba(78, 200, 237, 0.15)",
        borderColor: "rgba(78, 200, 237, 0.4)",
        transform: "translateY(-2px)",
      }
    },
    
    ".separator, span": {
      display: "none",
    }
  })}
`;

const Copyright = styled.p`
  color: white;
  font-size: 12px;
  font-style: italic;
  margin: 30px 20px 30px;
  text-align: left;
  ${mobile({
    textAlign: "center",
    margin: "32px 0px",
  })}
`;

const SocialMediaIcons = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 24px 0;
  gap: 12px;

  a {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 12px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 50%;
    border: 1px solid rgba(78, 200, 237, 0.2);
    transition: all 0.3s ease;
    
    &:hover {
      background: rgba(78, 200, 237, 0.15);
      border-color: rgba(78, 200, 237, 0.4);
      transform: translateY(-3px);
    }
  }

  .icon {
    color: white;
    margin: 0;
    transition: all 0.3s ease;
    font-size: 24px;

    &:hover {
      color: #4ec8ed;
    }
  }

  ${mobile({ 
    margin: "28px 0",
    gap: "8px",
    
    "a": {
      padding: "10px",
      background: "rgba(255, 255, 255, 0.05)",
      borderRadius: "50%",
      border: "1px solid rgba(78, 200, 237, 0.2)",
      
      "&:hover": {
        background: "rgba(78, 200, 237, 0.15)",
        borderColor: "rgba(78, 200, 237, 0.4)",
        transform: "translateY(-3px)",
      }
    },
    
    ".icon": {
      margin: "0",
      fontSize: "22px",
    }
  })}
`;

const Footer = () => {
  const currentYear = new Date().getFullYear();
  return (
    <FooterContainer>
      <FooterContent>
        <Logo>WHY SHOP WITH US?</Logo>
        <Links>
          <Link to="/about"> About Us </Link>
          <span className="separator">|</span>
          <span style={{ color: "#f0f0f0" }}>|</span>
          <Link to="/blog"> Blog </Link>
          <span className="separator">|</span>
          <span style={{ color: "#f0f0f0" }}>|</span>
          <Link to="/policy"> Privacy Policy </Link>
          <span className="separator">|</span>
          <span style={{ color: "#f0f0f0" }}>|</span>
          <Link to="/shipping_matrix"> Shipping Matrix </Link>
          <span className="separator">|</span>
          <span style={{ color: "#f0f0f0" }}>|</span>
          <a href="/my_profile/my_orders"> Order History </a>
          <span className="separator">|</span>
          <span style={{ color: "#f0f0f0" }}>|</span>
          <a
            href="https://lusuku.shop/vendorRegistration"
            target="_blank"
            rel="noopener noreferrer"
          >
            {" "}
            Become a Vendor{" "}
          </a>
          <span className="separator">|</span>
          <span style={{ color: "#f0f0f0" }}>|</span>
          <Link to="/payment_options"> Payment Options & Terms</Link>
          <span className="separator">|</span>
          <span style={{ color: "#f0f0f0" }}>|</span>
          <Link to="/omnichannel-inbox"> WhatsApp Integration </Link>
          <span className="separator">|</span>
          <span style={{ color: "#f0f0f0" }}>|</span>
          <a
            href="https://lusuku.shop/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Vendor Dashboard
          </a>
        </Links>
        <SocialMediaIcons>
          <a
            href={FACEBOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FacebookIcon className="icon" />
          </a>
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <InstagramIcon className="icon" />
          </a>
          <a
            href={TIKTOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Lusuku on TikTok"
          >
            {/* Not a Material UI icon: MUI has no TikTok glyph. TikTokIcon
                paints with currentColor and takes the same `icon` class, so it
                inherits this row's size, colour and hover without exception. */}
            <TikTokIcon className="icon" />
          </a>
          {/* X/Twitter removed: @lusukuonline was the same wrong handle that
              had been used for Instagram, and no X account was confirmed under
              it. Restore this block when the real handle is known. */}
          <a
            href={LINKEDIN_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <LinkedInIcon className="icon" />
          </a>
          <a
            href={`https://wa.me/${SUPPORT_WA_NUMBER}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <WhatsAppIcon className="icon" />
          </a>
        </SocialMediaIcons>
        <Copyright>
          Customer Care: <a href={CUSTOMER_CARE_TEL} style={{ color: "#4EC8ED", textDecoration: "none" }}>{CUSTOMER_CARE_NUMBER}</a>
          {"  ·  "}© All Rights Reserved {currentYear} ~ Lusuku
        </Copyright>
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer;
